import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dunningApi, paymentPlanApi, formatApiError } from '../services/api';
import { Warning, Receipt, EnvelopeSimple, DownloadSimple, CurrencyEur, Percent, Buildings, Calendar, X } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import PaymentPlanDialog from '../components/PaymentPlanDialog';

const fmtEUR = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
const LEVEL_STYLE = {
  1: 'bg-amber-100 text-amber-800',
  2: 'bg-orange-100 text-orange-800',
  3: 'bg-red-100 text-red-800',
};

export default function Dunning() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [filter, setFilter] = useState('eligible');
  const [planDialog, setPlanDialog] = useState(null); // invoice-like object

  const load = async () => {
    setLoading(true);
    try {
      const r = await dunningApi.overview();
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleSend = async (item) => {
    if (!window.confirm(`${item.next_stage_name} für Rechnung ${item.invoice_number} versenden?\n\nAn: ${item.customer_name}\nOffener Betrag: ${fmtEUR(item.open_amount)}\nGebühr Stufe: ${fmtEUR(item.next_stage_fee)}\nVerzugszinsen: ${item.charge_interest ? 'aktiv' : 'inaktiv'}`)) return;
    setSending(item.invoice_id);
    try {
      const r = await dunningApi.sendReminder(item.invoice_id);
      toast.success(`${r.data.label} versendet`, {
        description: `Gesamt fällig: ${fmtEUR(r.data.total_due)} · Neue Frist: ${r.data.new_due_date}`,
        duration: 8000,
      });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(null); }
  };

  const handleToggleInterest = async (item) => {
    try {
      const r = await dunningApi.toggleInterest(item.invoice_id, !item.charge_interest);
      toast.success(r.data.charge_dunning_interest ? 'Verzugszinsen aktiviert' : 'Verzugszinsen deaktiviert');
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleCancelPlan = async (item) => {
    if (!window.confirm(`Ratenplan für Rechnung ${item.invoice_number} wirklich löschen?\nMahnwesen läuft danach wieder normal weiter.`)) return;
    try {
      await paymentPlanApi.cancel(item.invoice_id);
      toast.success('Ratenplan entfernt');
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const items = (data?.items || []).filter(i => filter === 'all' ? true : i.eligible);
  const eligibleCount = (data?.items || []).filter(i => i.eligible).length;

  return (
    <div className="p-6 space-y-6" data-testid="dunning-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#111418]">Mahnwesen</h1>
          <p className="text-[#525866] mt-1">
            {data ? `${data.items.length} überfällige Rechnungen · Gesamt offen ${fmtEUR(data.total_open)}` : 'Wird geladen…'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant={filter === 'eligible' ? 'default' : 'outline'} onClick={() => setFilter('eligible')} data-testid="filter-eligible">
            Zum Versand bereit ({eligibleCount})
          </Button>
          <Button variant={filter === 'all' ? 'default' : 'outline'} onClick={() => setFilter('all')} data-testid="filter-all">
            Alle überfällig
          </Button>
        </div>
      </div>

      {/* Stages Config Preview */}
      {data?.stages && (
        <div className="card-swiss p-4" data-testid="stages-info">
          <p className="overline text-[#8A94A6] mb-2">Konfigurierte Mahnstufen</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.stages.map((s, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded border border-[#E2E4E9] bg-[#FAFAFB]">
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${LEVEL_STYLE[s.level] || ''}`}>Stufe {s.level}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#111418] truncate">{s.name}</p>
                  <p className="text-xs text-[#8A94A6]">ab {s.days_after_due} Tage · {fmtEUR(s.fee)} Gebühr</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#8A94A6] mt-2">Konfiguration in den Einstellungen anpassbar.</p>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="card-swiss h-40 skeleton" />
      ) : items.length === 0 ? (
        <div className="card-swiss p-12 text-center" data-testid="dunning-empty">
          <Receipt weight="thin" size={64} className="mx-auto text-[#8A94A6] mb-4" />
          <p className="text-[#525866]">Keine überfälligen Rechnungen. 🎉</p>
        </div>
      ) : (
        <div className="card-swiss overflow-hidden">
          <table className="table-swiss">
            <thead>
              <tr>
                <th>Rechnung</th>
                <th>Kunde</th>
                <th>Fällig</th>
                <th className="text-right">Offen</th>
                <th>Aktuelle Stufe</th>
                <th>Nächste Stufe</th>
                <th className="text-center">Zinsen</th>
                <th className="text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const overdueClass = i.days_overdue > 30 ? 'text-red-700 font-semibold' : i.days_overdue > 14 ? 'text-orange-700' : 'text-amber-700';
                return (
                  <tr key={i.invoice_id} data-testid={`dunning-row-${i.invoice_id}`}>
                    <td>
                      <Link to={`/invoices/${i.invoice_id}`} className="font-semibold text-[#111418] hover:underline">
                        {i.invoice_number}
                      </Link>
                    </td>
                    <td className="text-sm">
                      <div>{i.customer_name}</div>
                      <div className="text-xs text-[#8A94A6]">{i.customer_email}</div>
                    </td>
                    <td className="text-sm">
                      <div className="text-[#525866]">{i.due_date}</div>
                      <div className={`text-xs ${overdueClass}`}>{i.days_overdue} Tage überfällig</div>
                    </td>
                    <td className="text-right">
                      <div className="font-semibold">{fmtEUR(i.open_amount)}</div>
                      {i.dunning_fees_total > 0 && <div className="text-xs text-[#525866]">+ {fmtEUR(i.dunning_fees_total)} Gebühren</div>}
                    </td>
                    <td>
                      {i.current_level > 0
                        ? <span className={`text-xs px-2 py-1 rounded font-semibold ${LEVEL_STYLE[i.current_level] || 'bg-gray-100 text-gray-700'}`}>Stufe {i.current_level}</span>
                        : <span className="text-xs text-[#8A94A6]">—</span>}
                    </td>
                    <td>
                      {(() => {
                        const pp = i.payment_plan || {};
                        if (pp.active && pp.ontrack) {
                          return (
                            <div className="flex flex-col gap-1">
                              <span className="text-xs inline-block px-2 py-1 rounded font-semibold bg-emerald-100 text-emerald-800" data-testid={`plan-active-${i.invoice_id}`}>
                                Ratenplan aktiv
                              </span>
                              <span className="text-xs text-[#8A94A6]">
                                {pp.paid_count}/{pp.total_count} · nächste {pp.next_due}
                              </span>
                            </div>
                          );
                        }
                        if (pp.active && !pp.ontrack) {
                          return (
                            <div className="flex flex-col gap-1">
                              <span className="text-xs inline-block px-2 py-1 rounded font-semibold bg-red-100 text-red-800">
                                Rate überfällig ({pp.worst_overdue_days}T)
                              </span>
                              {i.next_stage_name && <div className={`text-xs inline-block px-2 py-1 rounded font-semibold ${LEVEL_STYLE[i.next_level] || 'bg-gray-100 text-gray-700'}`}>{i.next_stage_name}</div>}
                            </div>
                          );
                        }
                        return i.next_stage_name
                          ? <div>
                              <div className={`text-xs inline-block px-2 py-1 rounded font-semibold ${LEVEL_STYLE[i.next_level] || 'bg-gray-100 text-gray-700'}`}>{i.next_stage_name}</div>
                              {i.next_stage_fee > 0 && <div className="text-xs text-[#8A94A6] mt-1">+ {fmtEUR(i.next_stage_fee)} Gebühr</div>}
                            </div>
                          : <span className="text-xs text-[#8A94A6]">max. Stufe</span>;
                      })()}
                    </td>
                    <td className="text-center">
                      <button
                        onClick={() => handleToggleInterest(i)}
                        data-testid={`toggle-interest-${i.invoice_id}`}
                        className={`px-2 py-1 rounded text-xs font-semibold transition ${
                          i.charge_interest ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                        title="Verzugszinsen (BGB § 288) berechnen"
                      >
                        <Percent weight="bold" size={12} className="inline mr-1" />
                        {i.charge_interest ? 'aktiv' : 'aus'}
                      </button>
                    </td>
                    <td className="text-right space-x-1 whitespace-nowrap">
                      <a href={dunningApi.reminderPdfUrl(i.invoice_id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50 rounded" data-testid={`preview-pdf-${i.invoice_id}`}>
                        <DownloadSimple weight="bold" size={12} /> PDF
                      </a>
                      {i.payment_plan?.active ? (
                        <button
                          onClick={() => handleCancelPlan(i)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                          data-testid={`cancel-plan-${i.invoice_id}`}
                          title="Ratenplan entfernen"
                        >
                          <X weight="bold" size={12} /> Plan
                        </button>
                      ) : (
                        <button
                          onClick={() => setPlanDialog({
                            invoice_id: i.invoice_id,
                            invoice_number: i.invoice_number,
                            open_amount: i.open_amount,
                            customer_name: i.customer_name,
                          })}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 rounded"
                          data-testid={`open-plan-${i.invoice_id}`}
                          title="Ratenplan anlegen"
                        >
                          <Calendar weight="bold" size={12} /> Ratenplan
                        </button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => handleSend(i)}
                        disabled={!i.eligible || sending === i.invoice_id}
                        data-testid={`send-reminder-${i.invoice_id}`}
                        className="btn-primary"
                        title={i.payment_plan?.active && i.payment_plan?.ontrack ? 'Mahnwesen pausiert (Ratenplan aktiv)' : ''}
                      >
                        <EnvelopeSimple weight="bold" size={12} className="mr-1" />
                        {sending === i.invoice_id
                          ? 'sende…'
                          : (i.payment_plan?.active && i.payment_plan?.ontrack)
                            ? 'pausiert'
                            : i.eligible ? 'Senden' : 'noch zu früh'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <PaymentPlanDialog
        open={!!planDialog}
        invoice={planDialog}
        onClose={() => setPlanDialog(null)}
        onSaved={load}
      />
    </div>
  );
}
